/**
 * @file activity.schema.ts
 * @description Define o schema Zod para a entidade 'Activity'.
 * Cada documento representa uma instância de um ActivityTemplate, atribuída
 * a uma gestação específica, com seu próprio status, data de ação e resultado.
 */

import { z } from 'zod';

import { RecurrenceRuleSchema } from './recurrence-rule.schema';

// --- Helper Schemas & Enums ---

/**
 * Define o status de uma atividade na lista de tarefas da usuária.
 * Como são estados de um fluxo, usamos camelCase.
 */
const ActivityStatusEnum = z.enum(['todo', 'inProgress', 'done', 'canceled', 'missed']);

/**
 * Define uma data de ação, que pode ser apenas um dia ou um dia com horário específico.
 */
const ActionDateSchema = z.discriminatedUnion('type', [
  z.object({ type: z.literal('DATE_WITH_HOUR'), datetime: z.date() }),
  z.object({ type: z.literal('DATE_ONLY'), date: z.date() }),
]);

/**
 * Define as diferentes estruturas para um resultado de agendamento,
 * dependendo do tipo de provedor com o qual a atividade foi agendada.
 */
const SchedulingResultDataSchema = z.discriminatedUnion('providerType', [
  // Estrutura para agendamentos com profissionais individuais
  z.object({
    providerType: z.literal('INDIVIDUAL_PRACTITIONER'),
    providerId: z.string(),
    locationId: z.string(),
    preAppointmentNotes: z.string().nullable(),
    postAppointmentNotes: z.string().nullable(),
    feedbackId: z.string().nullable(),
  }),
  // Estrutura para agendamentos com laboratórios ou clínicas de imagem
  z.object({
    providerType: z.enum(['LABORATORY', 'IMAGING_CLINIC']),
    providerId: z.string(),
    locationId: z.string(),
    examPreparations: z.string().nullable(),
    fileUploadUrls: z.array(z.string()),
    feedbackId: z.string().nullable(),
  }),
]);

/**
 * Define os possíveis resultados de uma atividade, que variam conforme o tipo de fluxo.
 */
const ActivityResultSchema = z.discriminatedUnion('type', [
  z.object({
    type: z.literal('SCHEDULING'),
    data: SchedulingResultDataSchema,
  }),
  z.object({
    type: z.literal('QUESTIONNAIRE'),
    answers: z.array(z.object({
      questionId: z.string(),
      value: z.any(),
    })),
    score: z.number().optional(),
  }),
]);


// --- Main Activity Schema ---

const ActivitySchema = z.object({
  /**
   * O ID da gestação ('Pregnancy') à qual esta atividade pertence.
   * É a principal chave de vínculo.
   */
  pregnancyId: z.string(),

  /**
   * O ID do 'ActivityTemplate' que serviu de molde para esta atividade.
   * Permite buscar as configurações originais (ícone, categoria, etc.).
   */
  templateId: z.string(),

  /**
   * O título de exibição desta instância da atividade. Geralmente copiado do
   * template, mas pode ser customizado (ex: "Consulta com Dra. Ana").
   */
  title: z.string(),

  /**
   * O status atual da atividade no fluxo da usuária, indicando se está
   * pendente, em andamento, concluída, etc.
   */
  status: ActivityStatusEnum,

  /**
   * A data e, opcionalmente, a hora em que a ação deve ocorrer.
   * Utiliza um schema flexível para acomodar tanto eventos de dia inteiro
   * quanto eventos com horário marcado.
   */
  actionDate: ActionDateSchema,

  /**
   * A regra de recorrência para esta atividade. Preenchido apenas na
   * atividade "mãe" de uma série para acionar uma Cloud Function que cria as demais.
   */
  recurrence: RecurrenceRuleSchema.nullable(),

  /**
   * Se esta atividade foi gerada por uma recorrência, este campo
   * armazena o ID da atividade "mãe" original que continha a regra.
   */
  recurrenceParentId: z.string().nullable(),

  /**
   * Armazena o resultado da conclusão da atividade. É um campo nulo até que
   * a atividade seja concluída. A estrutura do resultado depende do tipo de
   * fluxo da atividade (agendamento, questionário, etc.).
   */
  result: ActivityResultSchema.nullable(),
  
  /**
   * Campos de auditoria para rastrear a criação e modificação da atividade.
   */
  createdAt: z.date(),
  updatedAt: z.date(),
  deletedAt: z.date().nullable(),
});


// --- Inferred TypeScript Types ---

type Activity = z.infer<typeof ActivitySchema>;
type ActivityStatus = z.infer<typeof ActivityStatusEnum>;
type ActionDate = z.infer<typeof ActionDateSchema>;
type ActivityResult = z.infer<typeof ActivityResultSchema>;
type SchedulingResultData = z.infer<typeof SchedulingResultDataSchema>;


// --- Public Exports ---

export {
  ActivitySchema,
  ActivityStatusEnum,
  ActionDateSchema,
  ActivityResultSchema,
  SchedulingResultDataSchema,
};

export type {
  Activity,
  ActivityStatus,
  ActionDate,
  ActivityResult,
  SchedulingResultData,
};